import { CKBadge, CKSearch } from 'clearkit';
import { useEffect, useState } from 'react';

import {
  SegmentLegacyTypeEnum,
  useUniversalSearchSegmentsQuery,
} from '~/generated/graphql';
import apollo from '~/lib/apollo';
import { validateDomain, validateEmail } from '~/lib/helpers';
import history from '~/lib/history';

import { isLinux, isMacOs, isWindows } from './getOperatingSystem';
import UniversalSearchCompanies from './queries/UniversalSearchCompanies.graphql';
import UniversalSearchPeople from './queries/UniversalSearchPeople.graphql';

type FilterSegmentsProps = {
  segments: any[];
  type: 'people' | 'companies';
  search: string;
  limit?: number;
};

const filterSegments = ({
  segments,
  type,
  search,
  limit = 3,
}: FilterSegmentsProps) => {
  const result: any[] = [];

  segments.forEach((segment: any) => {
    const { name, id } = segment.node;
    const data = { id, name, type };
    const haystack = name.toLowerCase();

    if (haystack === search) {
      result.unshift(data);
    } else if (haystack.indexOf(search) > -1) {
      result.push(data);
    }
  });

  return result.slice(0, limit);
};

type HandleLookupRecord = {
  query: string;
  type: 'email' | 'domain';
};

type UniversalSearchQueryResults = {
  people: any[];
  companies: any[];
  segments: any[];
  lookup: HandleLookupRecord[];
};

const useUniversalSearch = ({ booted = false }) => {
  const [initialized, setInitialized] = useState(false);
  useUniversalSearchSegmentsQuery;
  const { data: personSegments } = useUniversalSearchSegmentsQuery({
    variables: { type: SegmentLegacyTypeEnum.Person },
  });
  const { data: companySegments } = useUniversalSearchSegmentsQuery({
    variables: { type: SegmentLegacyTypeEnum.Company },
  });

  const handleLookup = (
    { record }: { record: HandleLookupRecord },
    _context: any,
  ) => {
    const { query, type } = record;
    if (type === 'email') {
      history.push(`/people/lookup/${query}`);
    } else if (type === 'domain') {
      history.push(`/companies/lookup/${query}`);
    }
  };

  const handleInputChange = async (query: string) => {
    const searchParam = query.trim().toLowerCase();

    const results: UniversalSearchQueryResults = {
      people: [],
      companies: [],
      segments: [],
      lookup: [],
    };

    try {
      const { data: peopleQueryResults } = await apollo.query({
        query: UniversalSearchPeople,
        variables: { query: searchParam },
      });

      if (peopleQueryResults?.people?.edges) {
        results.people = peopleQueryResults.people.edges.map((edge: any) => {
          return {
            id: edge.node.id,
            name: edge.node.name || edge.node.email,
            email: edge.node.email,
          };
        });
      }
    } catch {
      // ignore
    }

    try {
      const { data: companiesQueryResults } = await apollo.query({
        query: UniversalSearchCompanies,
        variables: { query: searchParam },
      });

      if (companiesQueryResults?.companies?.edges) {
        results.companies = companiesQueryResults.companies.edges.map(
          (edge: any) => {
            return {
              id: edge.node.id,
              name: edge.node.name || edge.node.domain,
              domain: edge.node.domain,
            };
          },
        );
      }
    } catch {
      // ignore
    }

    if (personSegments?.segments?.edges) {
      const segments = filterSegments({
        segments: personSegments.segments.edges,
        type: 'people',
        search: searchParam,
      });
      results.segments = results.segments.concat(segments);
    }

    if (companySegments?.segments?.edges) {
      const segments = filterSegments({
        segments: companySegments.segments.edges,
        type: 'companies',
        search: searchParam,
      });

      results.segments = results.segments.concat(segments);
    }

    if (results.people.length === 0 && results.companies.length === 0) {
      const mightBeEmail = validateEmail(searchParam);
      if (mightBeEmail) {
        results.lookup.push({ query: searchParam, type: 'email' });
        return results;
      }

      const mightBeCompany = validateDomain(searchParam);
      if (mightBeCompany) {
        results.lookup.push({ query: searchParam, type: 'domain' });
        return results;
      }
    }

    return results;
  };

  useEffect(() => {
    if (!booted || initialized) return;

    window.CommandBar.addRecords('people', [], {
      labelKey: 'name',
      searchableFields: ['name', 'email'],
    });
    window.CommandBar.addRecords('companies', [], {
      labelKey: 'name',
      searchableFields: ['name', 'domain'],
    });
    window.CommandBar.addRecords('segments', [], {
      labelKey: 'name',
      searchableFields: ['name'],
    });
    window.CommandBar.addRecords('lookup', [], {
      labelKey: 'query',
      searchableFields: ['query'],
    });
    window.CommandBar.addMultiSearch(handleInputChange, [
      'people',
      'companies',
      'segments',
      'lookup',
    ]);
    window.CommandBar.addCallback('handleUniversalSearchLookup', handleLookup);

    setInitialized(true);
  }, [booted, initialized]);
};

const UniversalSearch = ({
  className = 'grow',
  placeholder = 'Search for anything…',
}: {
  className?: string;
  placeholder: string;
}) => {
  const handleClick = (e: any) => {
    e.preventDefault();

    // Previously we were opening for a lookup command.
    // Now we're just opening the command bar and using search.
    window.CommandBar.open();
  };

  const onKeyDown = (e: any) => {
    e.preventDefault();

    // Might be nice to figure out if we can send the keystroke to CommandBar
    window.CommandBar.open();
  };

  return (
    <>
      <CKSearch
        className={className}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        title={placeholder}
      />
      {isMacOs || isWindows || isLinux ? (
        <CKBadge className="bg-gradient-to-br from-gray-600 to-gray-900">
          {isMacOs ? '⌘K' : 'Ctrl+K'}
        </CKBadge>
      ) : null}
    </>
  );
};

export { UniversalSearch, useUniversalSearch };
